import React, { useRef, useState, useEffect } from 'react';
import siteContent from '../../data/siteContent';
import CounterStat from '../common/CounterStat';
import useReveal from '../../hooks/useReveal';

function HeroSection() {
  useReveal();

  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState('0:00');
  const [duration, setDuration] = useState('0:00');
  const videoRef = useRef(null);
  const progressBarRef = useRef(null);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onTimeUpdate = () => {
      setProgress((video.currentTime / video.duration) * 100 || 0);
      setCurrentTime(formatTime(video.currentTime));
    };

    const onLoadedMetadata = () => {
      setDuration(formatTime(video.duration));
    };

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('loadedmetadata', onLoadedMetadata);

    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, []);

  const handleTogglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const handleToggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
  };

  const handleSeek = (e) => {
    const video = videoRef.current;
    const bar = progressBarRef.current;
    if (!video || !bar) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    video.currentTime = ratio * video.duration;
  };

  return (
    <section id="home" className="hero-section">
      <div className="hero-pattern hero-pattern-left" />
      <div className="hero-pattern hero-pattern-right" />
      <div className="hero-glow hero-glow-blue" />
      <div className="hero-glow hero-glow-pink" />

      <div className="hero-video-bg">
        <div className="hero-video-placeholder" />
      </div>

      <div className="hero-content">
        <div className="hero-eyebrow reveal">{siteContent.hero.eyebrow}</div>

        <h1 className="hero-title reveal">
          {siteContent.hero.title1}
          <br />
          {siteContent.hero.title2}{' '}
          <span className="accent">{siteContent.hero.titleAccent}</span>
          <br />
          {siteContent.hero.title3}
        </h1>

        <p className="hero-sub reveal">{siteContent.hero.subtitle}</p>

        <div className="hero-cta reveal">
          <a href="#produtos" className="btn-ghost">
            Ver o Produto ↓
          </a>
        </div>

        <div className="hero-video-embed reveal">
          <div className="hero-video-embed-inner">
            <video
              ref={videoRef}
              className="hero-real-video"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
            >
              <source src="/video.mp4" type="video/mp4" />
              Seu navegador não suporta vídeo.
            </video>

            {/* Barra de controles — fica na parte de baixo do vídeo */}
            <div className="video-controls-bar">
              {/* Play / Pause */}
              <button
                type="button"
                className="vc-btn"
                onClick={handleTogglePlay}
                aria-label={isPlaying ? 'Pausar vídeo' : 'Reproduzir vídeo'}
              >
                {isPlaying ? (
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <rect x="6" y="4" width="4" height="16" rx="1" />
                    <rect x="14" y="4" width="4" height="16" rx="1" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M8 5.14v13.72L19 12 8 5.14Z" />
                  </svg>
                )}
              </button>

              {/* Tempo atual */}
              <span className="vc-time">{currentTime}</span>

              {/* Barra de progresso */}
              <div
                ref={progressBarRef}
                className="vc-progress"
                onClick={handleSeek}
                role="slider"
                aria-label="Progresso do vídeo"
                aria-valuenow={Math.round(progress)}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="vc-progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>

              {/* Duração total */}
              <span className="vc-time">{duration}</span>

              {/* Mute / Unmute */}
              <button
                type="button"
                className="vc-btn"
                onClick={handleToggleMute}
                aria-label={isMuted ? 'Ativar som' : 'Desativar som'}
              >
                {isMuted ? (
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                    <path d="M11 5 6 9H2v6h4l5 4V5Z" fill="currentColor" stroke="none" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                    <path d="M11 5 6 9H2v6h4l5 4V5Z" fill="currentColor" stroke="none" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="hero-stats">
        {siteContent.hero.stats.map((item) => (
          <CounterStat key={item.label} target={item.target} label={item.label} />
        ))}
      </div>
    </section>
  );
}

export default HeroSection;